import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { Restoration } from 'models/order';
import InfoText from 'components/shared/info-text.component';
import {
    formatPropertyValue,
    formatTeeth,
    MATERIAL_KEY,
    propertyByKey,
} from 'components/order-details/main-order-view/restorations-section/restoration-format';

const OTHER_MATERIAL_KEY = 'OTHER_MATERIAL';
const SHADE_KEY = 'SHADE';
const COLOR_KEY = 'COLOR';
const COLOR_SAMPLING_KEY = 'COLOR_SAMPLING';

/** `1. Bridge — Teeth: 11 - 21`, or just the type when the restoration has no teeth selection. */
const restorationHeading = (restoration: Restoration, index: number, translate: TFunction): string => {
    const type = translate(`restoration-types:${restoration.type}`, { defaultValue: restoration.type });
    const teeth = formatTeeth(restoration.teeth, translate);
    return teeth ? `${index + 1}. ${type} — ${teeth}` : `${index + 1}. ${type}`;
};

/** The ordered material, falling back to the free-text one; both are shown when both were given. */
const orderedMaterial = (restoration: Restoration, translate: TFunction): string =>
    [
        formatPropertyValue(propertyByKey(restoration, MATERIAL_KEY), translate),
        formatPropertyValue(propertyByKey(restoration, OTHER_MATERIAL_KEY), translate),
    ]
        .filter((part) => part)
        .join(' · ');

/**
 * The order carries the colour as two properties: `SHADE` is the scale ("Vita Classic") and
 * `COLOR` is the chip within it ("A3.5"), so the scale on its own tells the technician nothing.
 * They are folded into one line the way the delivery note already prints them, and a clinic that
 * deliberately left the shade to the lab (`COLOR_SAMPLING`) is said so rather than left blank.
 */
const orderedShade = (restoration: Restoration, translate: TFunction): string => {
    const color = formatPropertyValue(propertyByKey(restoration, COLOR_KEY), translate);
    const shade = formatPropertyValue(propertyByKey(restoration, SHADE_KEY), translate);
    const parts: string[] = [];
    if (color && shade) {
        parts.push(`${color} (${shade})`);
    } else if (color || shade) {
        parts.push(color || shade);
    }
    if (propertyByKey(restoration, COLOR_SAMPLING_KEY)?.flag) {
        parts.push(translate('orders:COLOR_SAMPLING'));
    }
    return parts.join(' · ');
};

type LabelledValueProps = {
    label: string;
    value: string;
};

const LabelledValue: FC<LabelledValueProps> = ({ label, value }: LabelledValueProps) => (
    <Box display="flex" flexWrap="wrap">
        <strong>{label}:&nbsp;</strong>
        <Typography variant="body2" color="#555">
            {value}
        </Typography>
    </Box>
);

type OrderShadeInfoPanelProps = {
    restorations: Restoration[];
};

/**
 * DD-3830: what the clinic ordered, beside the milling form, so the technician never has to close
 * the dialog to look up the shade. Display only — it never writes into the form (PO decision).
 *
 * Every restoration is listed, including ones the clinic left blank, so the numbering matches the
 * order view and nothing looks hidden. The panel renders an order the page has already fetched into
 * the Apollo cache, so it owns no loading or error state — the page renders the loader and the error
 * boundary — and it is a pure function of its props because the dialog unmounts it on close.
 */
const OrderShadeInfoPanel: FC<OrderShadeInfoPanelProps> = ({ restorations }: OrderShadeInfoPanelProps) => {
    const { t: translate } = useTranslation(['milling-jobs', 'orders', 'restoration-types']);
    const notSpecified = translate('milling-jobs:NOT_SPECIFIED');

    return (
        <Box data-test="milling-jobs.order-shade-info-panel">
            <Typography variant="h4" component="h3" mb={1}>
                {translate('milling-jobs:ORDER_SHADE_AND_MATERIAL')}
            </Typography>
            {restorations.length > 0 ? (
                <Fragment>
                    {restorations.map((restoration, index) => (
                        <Card
                            key={restoration._id}
                            variant="outlined"
                            sx={{ mb: 1 }}
                            data-test="milling-jobs.order-shade-info-panel.restoration"
                        >
                            <Box p={1.5}>
                                <Typography variant="subtitle2" mb={0.5}>
                                    {restorationHeading(restoration, index, translate)}
                                </Typography>
                                <LabelledValue
                                    label={translate('orders:MATERIAL')}
                                    value={orderedMaterial(restoration, translate) || notSpecified}
                                />
                                <LabelledValue
                                    label={translate('orders:SHADE')}
                                    value={orderedShade(restoration, translate) || notSpecified}
                                />
                            </Box>
                        </Card>
                    ))}
                </Fragment>
            ) : (
                <InfoText text={translate('milling-jobs:NO_RESTORATIONS_ON_ORDER')} />
            )}
        </Box>
    );
};

export default OrderShadeInfoPanel;
